/**
 * The auto-scroll choreography around finishing a round.
 *
 * On phones the tall portrait chart pushes the verdict (stars/message) and
 * the Next button below the fold. When a round closes, the page settles for
 * a moment (the ship sails its homeward leg), then glides down to the
 * verdict; clicking Next glides back up so the whole chart is in view again
 * — same pause, same pace, mirrored.
 *
 * The browser's native `scrollIntoView({behavior:"smooth"})` pace isn't
 * tunable, so the glide is a small rAF animation with an ease-in-out curve.
 * Any user wheel/touch input aborts it (never fight the player), reduced
 * motion jumps instantly, and on desktop — where everything already fits —
 * the targets resolve to the current position and nothing moves.
 */
import { $, stageEl } from "../dom";

const DELAY_MS = 1000;
const DURATION_MS = 1100;

let delayTimer: number | null = null;
let animFrame: number | null = null;

const easeInOut = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const abort = (): void => stopAnim();
function addAbortListeners(): void {
  window.addEventListener("wheel", abort, { passive: true });
  window.addEventListener("touchmove", abort, { passive: true });
}
function removeAbortListeners(): void {
  window.removeEventListener("wheel", abort);
  window.removeEventListener("touchmove", abort);
}

function stopAnim(): void {
  if (animFrame !== null) { cancelAnimationFrame(animFrame); animFrame = null; }
  removeAbortListeners();
}

/** Cancel any pending or running auto-scroll (e.g. the route was reopened). */
export function cancelAutoScroll(): void {
  if (delayTimer !== null) { clearTimeout(delayTimer); delayTimer = null; }
  stopAnim();
}

function animateTo(targetTop: number): void {
  stopAnim();
  const maxTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  const to = Math.min(Math.max(0, targetTop), maxTop);
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    window.scrollTo(0, to);
    return;
  }
  const from = window.scrollY;
  if (Math.abs(to - from) < 2) return;
  const t0 = performance.now();
  addAbortListeners();
  const step = (now: number): void => {
    const t = Math.min(1, (now - t0) / DURATION_MS);
    window.scrollTo(0, from + (to - from) * easeInOut(t));
    if (t < 1) animFrame = requestAnimationFrame(step);
    else stopAnim();
  };
  animFrame = requestAnimationFrame(step);
}

/** Resolve the target lazily at fire time — the layout may shift meanwhile. */
function schedule(target: () => number): void {
  cancelAutoScroll();
  delayTimer = window.setTimeout(() => {
    delayTimer = null;
    animateTo(target());
  }, DELAY_MS);
}

/** After the round closes: settle, then glide down to center the verdict. */
export function scrollToVerdict(): void {
  schedule(() => {
    const r = $("nextBtn").getBoundingClientRect();
    return window.scrollY + r.top - (window.innerHeight - r.height) / 2;
  });
}

/** After Next (or a restart): glide back up to the full game field. */
export function scrollToChart(): void {
  schedule(() => window.scrollY + stageEl.getBoundingClientRect().top - 10);
}
